/* eslint-disable react/jsx-props-no-spreading */
import './polyfills';
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CircularProgress } from '@mui/material';

import './utils/styles';
import './css/styles';

import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { ToastContainer, Slide } from 'react-toastify';
import { store as ConfigureStore } from './store';
import { registerTilesInterceptor } from './services/tiles-token.service';
import { initializeAirbrake } from './utils/airbrake';
import theme from './theme';

import Tooltip from './shared/tooltip';
import { AppContext, initializeApp, createObjectCache } from './hooks/use-app';
import { SessionProvider } from './contexts/session-context';
import { FeatureFlagProvider } from './contexts/feature-flag-context';
import { ConfigProvider } from './contexts/config-context';
import { toastConfig } from './components/info-elements/toast-action';

import Sidenav from './sidenav';
import Plans from './plans';
import PermissionMembers from './permission-members';
import PermissionLayers from './permission-layers';
import MediaShare from './editor/media/media-share';
import Members from './members';
import LayerUploader from './layer-uploader';
import GroupsDetails from './groups/groups-details';
import Groups from './groups';
import ConfirmAuthButton from './profile';
import GlobalNav from './global-nav';
import AuthenticationCompliance from './authentication-compliance';
import FormBuilderReactBridge from './form-builder/bridge';
import FastFillSettingsHost from './form-builder/fast-fill-settings-host';

initializeAirbrake(window.AirbrakeConfig);

interface ComponentRegistry {
  [key: string]: React.ComponentClass | React.FunctionComponent | React.ReactNode;
}

const lazyComponents: ComponentRegistry = {
  ActivityFeed: React.lazy(() => import('./activity-feed/activity-feed')),
  AppBuilderHelper: React.lazy(() => import('./app-builder-helper/app-builder-helper')),
  AppDashboard: React.lazy(() => import('./app-dashboard')),
  AppDelete: React.lazy(() => import('./app-dashboard/app-delete')),
  AppPublish: React.lazy(() => import('./app-dashboard/app-publish')),
  ArcGISLoginButton: React.lazy(() => import('./arcGIS/arcGIS-login-button')),
  AttachmentModal: React.lazy(() => import('./attachments/attachment-modal')),
  AuditLogs: React.lazy(() => import('./audit-logs')),
  Authorizations: React.lazy(() => import('./authorizations')),
  BuildItForMe: React.lazy(() => import('./build-it-for-me')),
  ChangePlan: React.lazy(() => import('./change-plan')),
  // ConfirmAuthButton: React.lazy(() => import('./profile')),
  DataEventsEditor: React.lazy(() => import('./data-events-editor')),
  EarlyAccessFeatures: React.lazy(() => import('./early-access-features')),
  Empty: React.lazy(() => import('./shared/material_components/empty')),
  ProjectsList: React.lazy(() => import('./projects/projects-list')),
  EmptyAlt: React.lazy(() => import('./shared/empty-alt')),
  FormBuilderModals: React.lazy(() => import('./form-builder/modals/form-builder-modals')),
  // Groups: React.lazy(() => import('./groups')),
  // GroupsDetails: React.lazy(() => import('./groups/groups-details')),
  HomePage: React.lazy(() => import('./home-page')),
  Infographics: React.lazy(() => import('./kiss-clients/boards/infographics-landing-page')),
  MultiAppInsights: React.lazy(() => import('./kiss-clients/bots/insights-landing-page')),
  Issues: React.lazy(() => import('./issues')),
  // LayerUploader: React.lazy(() => import('./layer-uploader')),
  // MediaShare: React.lazy(() => import('./editor/media/media-share')),
  // Members: React.lazy(() => import('./members')),
  NewDataViewRouter: React.lazy(() => import('./data-view/new-data-view-router')),
  // PermissionLayers: React.lazy(() => import('./permission-layers')),
  // PermissionMembers: React.lazy(() => import('./permission-members')),
  // Plans: React.lazy(() => import('./plans')),
  PrintProgress: React.lazy(() => import('./print-progress')),
  PluginFrame: React.lazy(() => import('./plugins/frames/plugin-frame')),
  PluginPanel: React.lazy(() => import('./plugins/nav-panel')),
  ProjectsView: React.lazy(() => import('./projects/project-view')),
  RecordEditor: React.lazy(() => import('./editor/record-editor')),
  RecordEditorPreview: React.lazy(() => import('./editor/record-editor-preview')),
  RecordHistory: React.lazy(() => import('./record-history')),
  // @ts-ignore
  ReportBuilder: React.lazy(() => import('./report-builder/report-builder')),
  ReportTemplateManager: React.lazy(() => import('./report-builder/report-template-manager')),
  RolesViewList: React.lazy(() => import('./roles/roles-view-list')),
  SetUpForm: React.lazy(() => import('./set_up_form')),
  Staticmap: React.lazy(() => import('./staticmap')),
  Tasks: React.lazy(() => import('./tasks')),
  VerifyEmail: React.lazy(() => import('./verify-email')),
  Welcome: React.lazy(() => import('./welcome')),
  Webhooks: React.lazy(() => import('./webhooks')),
  Workflows: React.lazy(() => import('./app-dashboard/workflows')),
  WorkflowBuilder: React.lazy(() => import('./workflow-builder')),
  SaveTestAppButton: React.lazy(() => import('./test-app-version/save-test-app-button')),
};

const standardComponents = {
  AuthenticationCompliance,
  ConfirmAuthButton,
  FormBuilderReactBridge,
  FastFillSettingsHost,
  GlobalNav,
  Groups,
  GroupsDetails,
  LayerUploader,
  MediaShare,
  Members,
  PermissionLayers,
  PermissionMembers,
  Plans,
  Sidenav,
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-component]').forEach((element: Element) => {
    const { component, props } = (element as HTMLElement).dataset;

    let Component = lazyComponents[component || ''];

    if (!Component) {
      Component = standardComponents[component || ''];

      if (!Component) {
        throw new Error(`Component not found: ${component}`);
      }
    }

    const { params, ...globals } = JSON.parse(props || '{}');
    const app = initializeApp(globals, createObjectCache(), params);
    window.Fulcrum = {
      ...window.Fulcrum,
      globals,
      params,
      app,
    };

    const client = new QueryClient();
    const store = ConfigureStore(app.api.token);
    registerTilesInterceptor(store, [
      app.globals.config.tile_url,
      app.globals.config.layer_url ?? app.globals.config.tile_url,
    ]);

    ReactDOM.createRoot(element).render(
      <ThemeProvider theme={theme}>
        <Provider store={store}>
          <FeatureFlagProvider>
            <AppContext.Provider value={app}>
              <ConfigProvider>
                <SessionProvider>
                  <QueryClientProvider client={client}>
                    {/* @ts-ignore */}
                    <Suspense fallback={<CircularProgress />}><Component {...params} /></Suspense>
                    <Tooltip />
                    <ToastContainer transition={Slide} {...toastConfig} />
                  </QueryClientProvider>
                </SessionProvider>
              </ConfigProvider>
            </AppContext.Provider>
          </FeatureFlagProvider>
        </Provider>
      </ThemeProvider>,
    );

    // Cleanup the DOM so a massive props object isn't visible in the dev tools.
    // @ts-ignore
    delete element.dataset?.props;
  });
});
